export const SESSION_STORAGE_KEY = 'helix.session';
const accessKey = 'helix.accessToken';
const refreshKey = 'helix.refreshToken';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
}

export interface SessionSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
  /** Browser-session identity; rotation preserves it, a new login replaces it. */
  generation: string;
}

export function getSessionSnapshot(): SessionSnapshot | null {
  const saved = localStorage.getItem(SESSION_STORAGE_KEY);
  if (saved !== null) {
    try {
      const value: unknown = JSON.parse(saved);
      if (
        typeof value === 'object' &&
        value !== null &&
        'generation' in value &&
        typeof value.generation === 'string' &&
        value.generation &&
        'accessToken' in value &&
        (value.accessToken === null || typeof value.accessToken === 'string') &&
        'refreshToken' in value &&
        (value.refreshToken === null || typeof value.refreshToken === 'string') &&
        (value.accessToken || value.refreshToken)
      )
        return value as SessionSnapshot;
    } catch {
      // Malformed local data never authenticates a user or falls back to an old token.
    }
    return null;
  }

  const localAccess = localStorage.getItem(accessKey);
  const localRefresh = localStorage.getItem(refreshKey);
  const hasLocalPair = Boolean(localAccess || localRefresh);
  // Never combine tokens belonging to different legacy storage sessions.
  const accessToken = hasLocalPair ? localAccess : sessionStorage.getItem(accessKey);
  const refreshToken = hasLocalPair ? localRefresh : sessionStorage.getItem(refreshKey);
  if (!accessToken && !refreshToken) return null;
  const migrated = { accessToken, refreshToken, generation: crypto.randomUUID() };
  writeSession(migrated);
  removeLegacyTokens();
  return migrated;
}

export function getSessionGeneration(): string | null {
  return getSessionSnapshot()?.generation ?? null;
}

export function getAccessToken(): string | null {
  return getSessionSnapshot()?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return getSessionSnapshot()?.refreshToken ?? null;
}

export function hasSessionTokens(): boolean {
  return getSessionSnapshot() !== null;
}

export function setTokens(tokens: StoredTokens): void {
  writeSession({ ...tokens, generation: crypto.randomUUID() });
  removeLegacyTokens();
}

/** Never let a late refresh restore a logged-out session or overwrite another login. */
export function replaceTokensIfCurrent(expected: SessionSnapshot, tokens: StoredTokens): boolean {
  if (!isCurrentSnapshot(expected)) return false;
  writeSession({ ...tokens, generation: expected.generation });
  return true;
}

export function clearTokensIfCurrent(expected: SessionSnapshot): boolean {
  if (!isCurrentSnapshot(expected)) return false;
  clearTokens();
  return true;
}

export function clearTokens(): void {
  // A tombstone prevents old tabs/legacy keys from reviving a signed-out session.
  localStorage.setItem(SESSION_STORAGE_KEY, 'null');
  removeLegacyTokens();
}

function isCurrentSnapshot(expected: SessionSnapshot) {
  const current = getSessionSnapshot();
  return (
    current?.generation === expected.generation &&
    current.accessToken === expected.accessToken &&
    current.refreshToken === expected.refreshToken
  );
}

function writeSession(session: SessionSnapshot) {
  // Store the pair in one operation; another tab must not read half a rotation.
  localStorage.setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify({
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      generation: session.generation,
    }),
  );
}

function removeLegacyTokens() {
  localStorage.removeItem(accessKey);
  localStorage.removeItem(refreshKey);
  sessionStorage.removeItem(accessKey);
  sessionStorage.removeItem(refreshKey);
}
